import { useInfiniteQuery } from "@tanstack/react-query";
import { Camera, Loader2, MessageSquare, Search, X } from "lucide-react";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  listInspections,
  type InspectionRow,
  type InspectionStatus,
  type Verdict,
} from "@/api/okk_receiving";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { statusInfo, updText, VERDICTS, verdictInfo } from "@/lib/okk-receiving";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { useDebounced } from "@/lib/use-debounced";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/pages/order-journal/parts";

/** Вкладки-фильтры; ключ уходит в `?status=` — «назад» из карточки
 *  возвращает на ту же вкладку. */
const TABS: { key: InspectionStatus | "all"; title: string }[] = [
  { key: "new", title: "Новые" },
  { key: "in_progress", title: "В работе" },
  { key: "testing", title: "На тесте" },
  { key: "done", title: "Готово" },
  { key: "all", title: "Все" },
];

function tabFrom(raw: string | null): InspectionStatus | "all" {
  return TABS.some((t) => t.key === raw) ? (raw as InspectionStatus | "all") : "all";
}

/**
 * ОКК: список проверок приходов. Незавершённые сверху (сортирует сервер),
 * внутри — свежие сверху. Поиск — заказ, УПД, ПН, поставщик или товар.
 *
 * Счётчики вкладок — из `counts` ответа списка (по поиску, без фильтра статуса).
 */
export function OkkListPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const tab = tabFrom(params.get("status"));
  const [rawQuery, setRawQuery] = useState("");
  const query = useDebounced(rawQuery.trim(), 350);
  const status = tab === "all" ? null : tab;

  const listQ = useInfiniteQuery({
    queryKey: ["okk-inspections", tab, query],
    queryFn: ({ pageParam }) => listInspections({ status, q: query, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total && last.items.length > 0 ? loaded : undefined;
    },
  });
  const rows = listQ.data?.pages.flatMap((p) => p.items) ?? [];

  // Счётчики вкладок несёт сам ответ списка (`counts` — по поиску, без фильтра статуса).
  const counts = listQ.data?.pages[0]?.counts;

  return (
    <div className="mx-auto max-w-6xl space-y-3">
      <h1 className="text-xl font-semibold">ОКК: входной контроль</h1>

      <div role="tablist" aria-label="Статус проверки" className="flex flex-wrap gap-1">
        {TABS.map((t) => {
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (t.key === "all") next.delete("status");
                else next.set("status", t.key);
                setParams(next, { replace: true });
              }}
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm",
                tab === t.key
                  ? "border-primary bg-primary text-primary-foreground"
                  : "bg-background hover:bg-accent",
              )}
            >
              {t.title}
              <span className="ml-1.5 tabular-nums opacity-80" data-testid={`okk-tab-count-${t.key}`}>
                {listQ.isError ? "!" : counts ? counts[t.key] : "…"}
              </span>
            </button>
          );
        })}
      </div>

      <Card className="p-3">
        <label className="block">
          <span className="mb-1 block text-xs text-muted-foreground">Поиск</span>
          <span className="relative block">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder="Заказ, УПД, ПН, поставщик или товар"
              className="pl-8 pr-8"
              aria-label="Поиск проверок"
            />
            {rawQuery && (
              <button
                type="button"
                onClick={() => setRawQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-label="Очистить поиск"
              >
                <X className="size-4" />
              </button>
            )}
          </span>
        </label>
      </Card>

      {listQ.isLoading && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      {listQ.isError && (
        <Card role="alert" className="border-destructive/50 p-3 text-sm text-destructive">
          Не удалось загрузить проверки: {apiErrorText(listQ.error)}
        </Card>
      )}
      {listQ.isSuccess && rows.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {query ? `По запросу «${query}» проверок нет` : "Проверок нет"}
        </p>
      )}

      {rows.length > 0 && (
        <Card className="divide-y overflow-hidden">
          <div className="hidden gap-3 px-3 py-2 text-xs text-muted-foreground lg:flex">
            <span className="min-w-0 flex-1">Поставщик · заказ</span>
            <span className="w-44 shrink-0">УПД · ПН</span>
            <span className="w-28 shrink-0">Приёмка</span>
            <span className="w-56 shrink-0">Позиции</span>
            <span className="w-36 shrink-0">Статус</span>
          </div>
          {rows.map((r) => (
            <InspectionRowView key={r.id} row={r} />
          ))}
        </Card>
      )}

      {listQ.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            disabled={listQ.isFetchingNextPage}
            onClick={() => void listQ.fetchNextPage()}
          >
            {listQ.isFetchingNextPage && <Loader2 className="size-4 animate-spin" />}
            Показать ещё
          </Button>
        </div>
      )}
    </div>
  );
}

function InspectionRowView({ row }: { row: InspectionRow }): React.ReactElement {
  const d = row.delivery;
  const st = statusInfo(row.status);
  const verdicts = VERDICTS.filter((v) => (row.verdicts[v] ?? 0) > 0);
  return (
    <Link
      to={`/okk-receiving/${row.id}`}
      data-testid="okk-row"
      className={cn(
        "flex flex-col gap-1 px-3 py-2 text-sm transition-colors hover:bg-accent lg:flex-row lg:items-center lg:gap-3",
        row.overdue === true && "border-l-4 border-l-rose-500",
      )}
    >
      <span className="min-w-0 break-words lg:flex-1">
        <span className="font-semibold">{d.kontragent_name || "—"}</span>
        <span className="block text-xs text-muted-foreground">
          заказ {d.order_number}
          {d.organization_name ? ` · ${d.organization_name}` : ""}
        </span>
      </span>
      <span className="text-xs lg:w-44 lg:shrink-0">
        <span className={cn(d.upd_missing && "text-amber-700")}>{updText(d)}</span>
        <span className="block text-muted-foreground">
          {d.one_c_receipt_number ? `ПН ${d.one_c_receipt_number}` : "ПН нет"}
          {d.one_c_receipt_number && (d.receipt_posted ? " (проведена)" : " (не проведена)")}
        </span>
      </span>
      <span
        className="text-xs text-muted-foreground lg:w-28 lg:shrink-0"
        title={
          d.receiving_date
            ? `Поступление по заказу (плановая дата): ${fmtDate(d.receiving_date)}`
            : undefined
        }
      >
        <span className="lg:hidden">приёмка </span>
        {d.completed_at ? fmtDateTime(d.completed_at) : "—"}
      </span>
      <span className="flex flex-wrap items-center gap-1 text-xs lg:w-56 lg:shrink-0">
        <span className="tabular-nums" title="С вердиктом / всего позиций">
          {row.items_with_verdict}/{row.items_total} поз.
        </span>
        {verdicts.map((v) => (
          <VerdictCount key={v} verdict={v} count={row.verdicts[v] ?? 0} />
        ))}
        {row.photos_count > 0 && (
          <span className="inline-flex items-center gap-0.5 text-muted-foreground" title="Фото">
            <Camera className="size-3.5" />
            {row.photos_count}
          </span>
        )}
        {row.comments_count > 0 && (
          <span
            className="inline-flex items-center gap-0.5 text-muted-foreground"
            title="Комментарии"
          >
            <MessageSquare className="size-3.5" />
            {row.comments_count}
          </span>
        )}
      </span>
      <span className="flex flex-wrap items-center gap-1 lg:w-36 lg:shrink-0 lg:flex-col lg:items-start">
        <StatusBadge tone={st.tone}>{st.text}</StatusBadge>
        {row.nearest_test_until && (
          <span className="text-xs text-muted-foreground">
            на тесте до {fmtDate(row.nearest_test_until)}
          </span>
        )}
        {row.overdue === true && (
          <span className="text-xs font-medium text-rose-700" data-testid="okk-overdue">
            просрочена
          </span>
        )}
        {row.samples_pending && (
          <span data-testid="okk-samples-pending">
            <StatusBadge tone="warn">образцы не оформлены</StatusBadge>
          </span>
        )}
      </span>
    </Link>
  );
}

function VerdictCount({ verdict, count }: { verdict: Verdict; count: number }): React.ReactElement {
  const info = verdictInfo(verdict);
  return (
    <StatusBadge tone={info.tone}>
      {info.short} {count}
    </StatusBadge>
  );
}
